import { useState, useMemo } from 'react';

interface ColumnDef {
    key: string;
    label: string;
    sortable?: boolean;
    align?: 'left' | 'right' | 'center';
    format?: (value: unknown, row: Record<string, unknown>) => string;
    /** Clé à lire dans `totals` pour afficher la ligne de pied de tableau. */
    totalKey?: string;
    /** Formatter dédié au footer (sinon fallback sur `format`). */
    formatTotal?: (value: number) => string;
}

interface SortableTableProps {
    columns: ColumnDef[];
    data: Record<string, unknown>[];
    onRowClick?: (row: Record<string, unknown>) => void;
    /** Map clé → valeur numérique agrégée (somme ou compte). Affiche un <tfoot> si fourni. */
    totals?: Record<string, number>;
    /** Libellé de la première cellule du footer. Par défaut : "Total". */
    totalLabel?: string;
}

type SortDir = 'asc' | 'desc' | null;

function formatCell(value: unknown, col: ColumnDef, row: Record<string, unknown>): string {
    if (value === null || value === undefined) return '—';
    if (col.format) return col.format(value, row);
    if (typeof value === 'number') return value.toLocaleString('fr-FR');
    return String(value);
}

function isNegative(value: unknown): boolean {
    return typeof value === 'number' && value < 0;
}

export function SortableTable({ columns, data, onRowClick, totals, totalLabel }: SortableTableProps) {
    const [sortKey, setSortKey] = useState<string | null>(null);
    const [sortDir, setSortDir] = useState<SortDir>(null);

    const handleSort = (key: string) => {
        if (sortKey === key) {
            if (sortDir === 'asc') setSortDir('desc');
            else if (sortDir === 'desc') { setSortKey(null); setSortDir(null); }
            else setSortDir('asc');
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
    };

    const sorted = useMemo(() => {
        if (!sortKey || !sortDir) return data;
        return [...data].sort((a, b) => {
            const va = a[sortKey];
            const vb = b[sortKey];
            if (va == null && vb == null) return 0;
            if (va == null) return 1;
            if (vb == null) return -1;
            const cmp = typeof va === 'number' && typeof vb === 'number'
                ? va - vb
                : String(va).localeCompare(String(vb), 'fr');
            return sortDir === 'asc' ? cmp : -cmp;
        });
    }, [data, sortKey, sortDir]);

    const sortIndicator = (key: string) => {
        if (sortKey !== key) return ' ↕';
        return sortDir === 'asc' ? ' ↑' : ' ↓';
    };

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                        {columns.map((col) => (
                            <th
                                key={col.key}
                                className={`px-3 py-2 font-medium text-gray-600 text-${col.align ?? 'left'}${col.sortable !== false ? ' cursor-pointer select-none hover:text-gray-900' : ''}`}
                                onClick={col.sortable !== false ? () => handleSort(col.key) : undefined}
                            >
                                {col.label}
                                {col.sortable !== false && (
                                    <span className="text-gray-400 text-xs">{sortIndicator(col.key)}</span>
                                )}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sorted.length === 0 && (
                        <tr>
                            <td colSpan={columns.length} className="px-3 py-8 text-center text-gray-400">
                                Aucune donnée
                            </td>
                        </tr>
                    )}
                    {sorted.map((row, i) => (
                        <tr
                            key={i}
                            className={`border-b border-gray-100 hover:bg-gray-50${onRowClick ? ' cursor-pointer' : ''}`}
                            onClick={onRowClick ? () => onRowClick(row) : undefined}
                        >
                            {columns.map((col) => {
                                const val = row[col.key];
                                const negative = isNegative(val);
                                return (
                                    <td
                                        key={col.key}
                                        className={`px-3 py-2 text-${col.align ?? 'left'}${negative ? ' text-red-600 font-medium' : ''}`}
                                    >
                                        {formatCell(val, col, row)}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
                {totals && (
                    <tfoot>
                        <tr className="bg-gray-100 border-t-2 border-gray-300 font-medium">
                            {columns.map((col, idx) => {
                                const raw = col.totalKey ? totals[col.totalKey] : undefined;
                                const hasTotal = col.totalKey && raw != null;
                                let content: string | null = null;
                                if (hasTotal) {
                                    content = col.formatTotal
                                        ? col.formatTotal(raw as number)
                                        : col.format
                                            ? col.format(raw, {})
                                            : (raw as number).toLocaleString('fr-FR');
                                } else if (idx === 0) {
                                    content = totalLabel ?? 'Total';
                                }
                                return (
                                    <td
                                        key={col.key}
                                        className={`px-3 py-2 text-${col.align ?? 'left'}`}
                                    >
                                        {content}
                                    </td>
                                );
                            })}
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
    );
}
